<template>
  <div class="home-page">
    <!-- 1. 文化內容生成平台 Section -->
    <HomeSplinePng/>

    <!-- 四大功能區塊的共用背景容器 -->
    <div class="features-background-container">
      <!-- 2. 功能導航 Section -->
      <nav class="feature-navigation-section d-none d-lg-block" :class="{ en: $i18n.locale === 'en' }">
        <div class="container">
          <div class="nav-buttons-wrapper">
            <button 
              v-for="(feature, index) in features" 
              :key="index" 
              :class="['btn block-btn nav-feature-btn', { active: activeFeature === index }]" 
              @click="scrollToFeature(index)"
            >
              {{ feature.name }}
            </button>
          </div>
        </div>
      </nav>
      
      <!-- 3. 四大功能介紹區塊 -->
      <section 
        v-for="(feature, index) in features" 
        :key="index" 
        :id="feature.id" 
        :class="['feature-section', feature.style]"
      >
        <div class="container-fluid feature-container">
          <!-- 卡片式佈局 -->
          <div v-if="feature.isCard" class="feature-card">
            <div class="row align-items-stretch">
              <div :class="feature.name === $t('imageCheckerTitle') ? 'col-md-4' : 'col-md-5'">
                <div class="feature-card-text" :class="{ 'image-checker-content': feature.name === $t('imageCheckerTitle') }">
                  <div class="feature-header">
                    <h3 class="feature-title">{{ feature.title }}</h3>
                  </div>
                  <div class="feature-description" :style="feature.name === $t('imageCheckerTitle') ? 'padding-right: 25px;' : ''">
                    <p>{{ feature.descriptions[0] }}</p>
                  </div>
                  <div v-if="feature.hasLLM" class="llm-section">
                    <h3 class="llm-title">{{ $t('llmChatWindow') }}</h3>
                    <div class="feature-description">
                      <p>{{ feature.descriptions[1] }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div :class="feature.name === $t('imageCheckerTitle') ? 'col-md-8' : 'col-md-7'">
                <div class="image-display-area">
                  <!-- 多語系響應式圖片 -->
                  <picture class="responsive-feature-image">
                    <!-- 電腦版 (992px 以上) -->
                    <source 
                      media="(min-width: 992px)" 
                      :srcset="getFeatureImage(feature, 'desktop')"
                    >
                    <!-- 平板版 (768px - 991px) -->
                    <source 
                      media="(min-width: 768px)" 
                      :srcset="getFeatureImage(feature, 'tablet')"
                    >
                    <!-- 手機版 (767px 以下) -->
                    <img 
                      :src="getFeatureImage(feature, 'mobile')" 
                      :alt="feature.title + ' 功能示意圖'" 
                      class="feature-mockup-img"
                    >
                  </picture>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 標準式佈局 -->
          <div v-else class="feature-standard">
            <div class="row">
              <!-- 文字內容在上方 -->
              <div class="col-12 text-section text-center" :class="{ 'text-center': feature.name === $t('imageUpscaleTitle') }">
                <div class="feature-header">
                  <h3 class="feature-title">{{ feature.title }}</h3>
                </div>
                <p class="feature-description">{{ feature.descriptions[0] }}</p>
              </div>
              
              <!-- 圖片/影片/對比器內容在下方 -->
              <div class="col-12 media-section">
                <!-- 影片 -->
                <div v-if="feature.isVideo" class="feature-video-container">
                  <div class="feature-video">
                    <div class="play-button">▶</div>
                  </div>
                </div>
                <!-- 圖片對比器 -->
                <div v-else-if="feature.isComparison" class="feature-comparison-container">
                  <HomeSliderComparison />
                </div>
                <!-- 圖片 -->
                <div v-else class="feature-visual">
                  <div :class="['mockup-image', feature.mockupClass]">
                    <!-- 多語系響應式圖片 -->
                    <picture class="responsive-feature-image">
                      <source 
                        media="(min-width: 992px)" 
                        :srcset="getFeatureImage(feature, 'desktop')"
                      >
                      <source 
                        media="(min-width: 768px)" 
                        :srcset="getFeatureImage(feature, 'tablet')"
                      >
                      <img 
                        :src="getFeatureImage(feature, 'mobile')" 
                        :alt="feature.title + ' 功能示意圖'" 
                        class="feature-mockup-img"
                      >
                    </picture>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 新增：微電影區塊 -->
      <section class="micro-film-section">
        <div class="container-fluid feature-container">
          <div class="micro-film-wrapper">

            <!-- 影片容器 -->
            <div class="film-video-container">
              <video class="micro-film-video" controls preload="metadata">
                <source src="/videos/memory.webm" type="video/webm">
                <source src="/videos/memory.mp4" type="video/mp4">

                <track
                  kind="captions"
                  src="/videos/memory.zh-TW.vtt"
                  srclang="zh-TW"
                  label="繁體中文"
                >

                  <track
                  kind="descriptions"
                  src="/videos/memory-descriptions.zh-TW.vtt"
                  srclang="zh-TW"
                  label="繁體中文口述影像"
                >

                您的瀏覽器不支援影片播放。
              </video>
            </div>

            <!-- 影片描述 -->
            <div class="film-description text-center">
              <p>{{ $t('microFilmDescription') }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 4. 資料來源 Section -->
      <section class="data-source-section">
        <div class="container-fluid feature-container">
          <div class="data-source-card">
            <img src="/images/ellipse_red.png" alt="">
            <p>{{ $t('dataSourceText') }}</p>
          </div>
        </div>
      </section>

    </div>
    
  </div>
</template>

<script>
import { isLoggedIn } from '@/auth'
import HomeSliderComparison from '@/components/HomeSliderComparison.vue'
import HomeSplinePng from '@/components/HomeSplinePng.vue'

export default {
  name: 'HomePage',
  components: {
    HomeSplinePng,
    HomeSliderComparison,
  },
  data() {
    return {
      activeFeature: 0,
      // 儲存所有功能的圖片路徑（包含多語系）
      featureImagesConfig: {
        generation: {
          desktop: {
            zh: '/images/desktop/generation_zh.png',
            en: '/images/desktop/generation_en.png'
          },
          tablet: {
            zh: '/images/tablet/generation_zh.png',
            en: '/images/tablet/generation_en.png'
          },
          mobile: {
            zh: '/images/mobile/generation_zh.png',
            en: '/images/mobile/generation_en.png'
          }
        },
        upscale: {
          desktop: {
            zh: '/images/desktop/upscale_zh.png',
            en: '/images/desktop/upscale_en.png'
          },
          tablet: {
            zh: '/images/tablet/upscale_zh.png',
            en: '/images/tablet/upscale_en.png'
          },
          mobile: {
            zh: '/images/mobile/upscale_zh.png',
            en: '/images/mobile/upscale_en.png'
          }
        },
        inpainting: {
          desktop: {
            zh: '/images/desktop/inpainting_zh.png',
            en: '/images/desktop/inpainting_en.png'
          },
          tablet: {
            zh: '/images/tablet/inpainting_zh.png',
            en: '/images/tablet/inpainting_en.png'
          },
          mobile: {
            zh: '/images/mobile/inpainting_zh.png',
            en: '/images/mobile/inpainting_en.png'
          }
        },
        checker: {
          desktop: {
            zh: '/images/desktop/checker_zh.png',
            en: '/images/desktop/checker_en.png'
          },
          tablet: {
            zh: '/images/tablet/checker_zh.png',
            en: '/images/tablet/checker_en.png'
          },
          mobile: {
            zh: '/images/mobile/checker_zh.png',
            en: '/images/mobile/checker_en.png'
          }
        }
      }
    }
  },
  computed: {
    isLoggedIn() {
      return isLoggedIn.value
    },
    // 計算當前語言
    currentLang() {
      return this.$i18n.locale === 'en' ? 'en' : 'zh'
    },
    // 計算 features 數組
    features() {
      return [
        {
          name: this.$t('imageGenerationTitle'),
          id: 'feature-0',
          imageKey: 'generation', // 用於查找圖片配置
          title: this.$t('imageGenerationTitle'),
          style: 'feature-card-style',
          isCard: true,
          hasLLM: false,
          descriptions: [
            this.$t('imageGenerationDesc1'),
            this.$t('imageGenerationDesc2')
          ]
        },
        {
          name: this.$t('imageUpscaleTitle'),
          id: 'feature-1',
          imageKey: 'upscale',
          title: this.$t('imageUpscaleTitle'),
          style: 'feature-standard-style',
          isCard: false,
          isVideo: false,
          isComparison: true,
          descriptions: [
            this.$t('imageUpscaleDesc')
          ]
        },
        {
          name: this.$t('imageInpaintingTitle'),
          id: 'feature-2',
          imageKey: 'inpainting',
          title: this.$t('imageInpaintingTitle'),
          style: 'feature-standard-style',
          isCard: false,
          isVideo: false,
          descriptions: [
            this.$t('imageInpaintingDesc')
          ]
        },
        {
          name: this.$t('imageCheckerTitle'),
          id: 'feature-3',
          imageKey: 'checker',
          title: this.$t('imageCheckerTitle'),
          style: 'feature-card-style',
          isCard: true,
          hasLLM: false,
          descriptions: [
            this.$t('imageCheckerDesc')
          ]
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    /**
     * 根據功能、尺寸和當前語言獲取圖片路徑
     * @param {Object} feature - 功能對象
     * @param {string} size - 圖片尺寸 (desktop/tablet/mobile)
     * @returns {string} 圖片路徑
     */
    getFeatureImage(feature, size) {
      const imageKey = feature.imageKey
      const lang = this.currentLang
      
      // 從配置中獲取對應的圖片路徑
      const imagePath = this.featureImagesConfig[imageKey]?.[size]?.[lang]
      
      // 如果找不到對應的圖片，返回預設圖片
      return imagePath || `/images/${size}/default.png`
    },
    
    scrollToFeature(index) {
      this.activeFeature = index
      const element = document.getElementById(this.features[index].id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    
    handleScroll() {
      const scrollPosition = window.scrollY - window.innerHeight / 3
      this.features.forEach((feature, index) => {
        const element = document.getElementById(feature.id)
        if (element) {
          const elementTop = element.offsetTop
          const elementBottom = elementTop + element.offsetHeight
          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            this.activeFeature = index
          }
        }
      })
    }
  }
}
</script>

<style scoped>
/* 基礎樣式 */
.home-page { background-image: url('/public/images/desktop/BG_banner.png'), url('/public/images/BG_3.png'); background-size: 100%; background-position: top, bottom; background-repeat: no-repeat; min-height: calc(100vh - 56px); }
.feature-navigation-section { background-color: transparent; padding: 20px 0; position: sticky; top: 0; margin: 0 auto; width: 45%; z-index: 100; }
.feature-navigation-section.en { width: 60%; }
.nav-buttons-wrapper { display: flex; justify-content: space-between; background-color: #212121; border-radius: 25px; padding: 4px; width: 100%; margin: 0 auto; gap: 2px; }
.nav-feature-btn { background: transparent; color: #fff; font-weight: 500; transition: all 0.2s ease; white-space: nowrap; }
.nav-feature-btn:hover { color: #e0e0e0; }
.nav-feature-btn.active { background-color: #ffffff; color: #000000; font-weight: 500; }
.features-background-container { background-image: url('/public/images/BG_2.png'); background-size: contain; background-position: 0 130px; background-repeat: no-repeat; position: relative; }
.features-background-container > .feature-section { position: relative; z-index: 2; }
.feature-section { padding: 30px 0; display: flex; align-items: center; background: transparent; }
.feature-standard-style, .feature-card-style { background: transparent; padding: 30px 0; }
.feature-standard { padding: 0; }
.feature-container { padding: 0 60px; }
.feature-card { background: #ffffff; border-radius: 25px; padding: 40px; width: 100%; }
.feature-card-text { display: flex; flex-direction: column; justify-content: center; height: 100%; padding: 0 1rem; }
.feature-header { margin-bottom: 30px; }
.feature-title { color: #FF7729; margin-bottom: 0; letter-spacing: 4px; }
.feature-subtitle { color: #FF7729; margin-bottom: 1rem; letter-spacing: 2px; }
.feature-description { color: #000; margin-bottom: 0; }
.text-section { padding: 30px 0 0; text-align: left; }
.media-section { padding: 20px 0; }
.feature-video-container { display: flex; justify-content: center; align-items: center; }
.feature-video { width: 100%; height: 450px; background-color: #999; border-radius: 15px; display: flex; align-items: center; justify-content: center; position: relative; cursor: pointer; transition: all 0.3s ease; }
.feature-video:hover { transform: translateY(-5px); }
.play-button { color: rgba(255, 255, 255, 0.9); font-size: 4rem; background: rgba(0, 0, 0, 0.3); border-radius: 50%; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; padding-left: 5px; transition: all 0.3s ease; }
.play-button:hover { background: rgba(0, 0, 0, 0.5); transform: scale(1.1); }
.feature-comparison-container { display: flex; justify-content: center; align-items: center; padding: 20px 0; }
.data-source-section { background: transparent; padding: 30px 0 60px; margin: 0; }
.data-source-card { display: flex; background: #ffffff; border-radius: 25px; padding: 40px; margin: 0; }
.data-source-card img { width: 28px; height: 100%; object-fit: contain; margin-right: 1rem; margin-bottom: 1rem; }
.data-source-card p { margin: 0; }
.feature-visual { display: flex; justify-content: center; align-items: center; }
.mockup-image { transition: transform 0.3s ease; position: relative; overflow: hidden; }
.mockup-image:hover { transform: translateY(-10px); }
.llm-section { padding-top: 40px; }
.llm-title { font-size: 1.1rem; font-weight: 700; color: #333; border-bottom: 1px solid #cbcbcb; padding-bottom: 10px; margin-bottom: 15px; }
.image-display-area { background-color: transparent; display: flex; align-items: center; justify-content: center; overflow: hidden; position: relative; height: 100%; }
.responsive-feature-image { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.feature-mockup-img { max-width: 100%; max-height: 100%; object-fit: contain; transition: transform 0.3s ease; }
.feature-mockup-img:hover { transform: scale(1.02); }
.mockup-image .feature-mockup-img { width: 100%; height: 100%; object-fit: cover; }

/* 微電影區塊樣式 */
.micro-film-section { background: transparent; padding: 60px 0; position: relative; z-index: 2; }
.micro-film-wrapper { max-width: 1200px; margin: 0 auto; }
.film-header { margin-bottom: 40px; }
.film-title { color: #FF7729; margin-bottom: 15px; }
.film-subtitle { margin-bottom: 0; }
.film-video-container { position: relative; width: 100%; background: #000; border-radius: 25px; overflow: hidden; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2); aspect-ratio: 16 / 9; }
.micro-film-video { width: 100%; height: 100%; object-fit: contain; background: #000; display: block; outline: none; }
.film-description { margin-top: 30px; padding: 0 20px; }
.film-description p { max-width: 80%; margin: 0 auto; text-align: justify; }
.micro-film-video:fullscreen, .micro-film-video:-webkit-full-screen, .micro-film-video:-moz-full-screen { width: 100%; height: 100%; object-fit: contain; }
.micro-film-video:focus { outline: 2px solid #FF7729; outline-offset: 4px; }

/* 平板響應式 (992px) */
@media (max-width: 992px) {
  .nav-buttons-wrapper { flex-wrap: wrap; justify-content: center; gap: 2px; padding: 4px; }
  .feature-section { padding: 0; }
  .feature-card-style { padding: 40px 0; }
  .feature-standard { padding: 30px 20px; }
  .feature-container { padding: 0 30px; }
  .feature-card { margin-bottom: 30px; padding: 30px; }
  .text-section { padding: 30px 0 0; text-align: center; }
  .media-section { padding: 10px 0; }
  .feature-video { height: 300px; }
  .data-source-card { padding: 30px 20px; }
  .micro-film-section { padding: 50px 0; }
  .film-header { margin-bottom: 30px; }
}

/* 手機響應式 (768px) */
@media (max-width: 768px) {
  .home-page { background-image: url('/public/images/mobile/BG_banner.png'), url('/public/images/BG_3.png'); }
  .nav-buttons-wrapper { flex-direction: column; border-radius: 15px; gap: 2px; }
  .nav-feature-btn { flex: none; width: 100%; text-align: center; }
  .features-background-container { background-image: url('/public/images/mobile/BG_feature1.png'), url('/public/images/mobile/BG_feature2.png'); background-size: contain; background-position: 0 130px, 0 1400px; background-repeat: no-repeat; }
  .feature-section { padding: 0; }
  .feature-card-style { padding: 30px 0; }
  .feature-standard { padding: 25px 15px; }
  .feature-container { padding: 0 18px; }
  .feature-card { padding: 30px; }
  .feature-title { font-size: 1.8rem; }
  .feature-video { height: 250px; }
  .image-checker-content { text-align: center; }
  .play-button { font-size: 3rem; width: 60px; height: 60px; }
  .data-source-card { padding: 25px 15px; flex-direction: column; }
  .image-display-area { margin-top: 20px; }
  .content-area { padding: 40px 20px; }
  .micro-film-section { padding: 40px 0; }
  .film-title { font-size: 1.8rem; letter-spacing: 2px; margin-bottom: 10px; }
  .film-header { margin-bottom: 25px; }
  .film-video-container { border-radius: 15px; }
  .film-description { margin-top: 20px; padding: 0 15px; }
  .film-description p { max-width: 100%; }
}

/* 小手機響應式 (480px) */
@media (max-width: 480px) {
  .film-video-container { border-radius: 12px; }
  .film-description p { max-width: 100%; }
}
</style>